import React, { useState, useRef, useEffect } from 'react';
import { Check } from 'lucide-react';
import styles from './TournamentBracket.module.scss';

const DEFAULT_AVATAR = 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y&s=400';

const isValidAvatar = (u?: any) =>
  typeof u === 'string' && u.trim() !== '' && !/^null|undefined$/i.test(String(u));

const getAvatarFromPlayer = (p?: any) => {
  const u =
    p?.profilePic ??
    p?.avatar ??
    p?.photoUrl ??
    p?.imageUrl ??
    p?.picture ??
    p?.profilePicture ??
    '';
  return isValidAvatar(u) ? String(u) : DEFAULT_AVATAR;
};

interface Player {
  id?: string | number;
  customerProfileId?: string;
  userId?: string;
  name?: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  skillLevel?: string;
  email?: string;
}

interface Match {
  id?: string | number;
  match_id?: string | number;
  match_number?: number;
  player1?: Player | null;
  player2?: Player | null;
  winner?: Player | null;
  score?: string;
  status?: string;
  table_number?: number;
}

interface Round {
  round_id?: string | number;
  id?: string | number;
  round_number?: number;
  roundNumber?: number;
  display_name?: string;
  displayName?: string;
  name?: string;
  matches?: Match[];
  completed_matches?: number;
  total_matches?: number;
  status?: string;
}

interface TournamentBracketProps {
  tournamentName?: string;
  rounds?: Round[];
  players?: Player[];
  tournamentStatus?: any;
}

const TournamentBracket: React.FC<TournamentBracketProps> = ({ 
  tournamentName = 'Mumbai Open 8-Ball Championship',
  rounds = [],
  players = [],
  tournamentStatus
}) => {
  const [zoom, setZoom] = useState(100);
  const bracketRef = useRef<HTMLDivElement>(null);

  // Process rounds data
  const processedRounds = React.useMemo(() => {
    if (!rounds || rounds.length === 0) {
      // Return empty structure if no rounds
      return [];
    }

    return rounds.map((round) => {
      const roundName = round.display_name || round.displayName || round.name || `Round ${round.round_number || round.roundNumber || ''}`;
      const matches = round.matches || [];
      const completed = matches.filter((m: Match) => m.status === 'completed').length;
      const total = matches.length;

      return {
        ...round,
        name: roundName,
        completed,
        total,
        matches: matches.map((match: Match) => {
          // Find player data from players array
          const findPlayer = (playerRef: Player | null | undefined): Player | null => {
            if (!playerRef) return null;
            const playerId = playerRef.id || playerRef.customerProfileId || playerRef.userId;
            if (!playerId) return playerRef;
            
            const found = players.find((p: Player) => 
              p.id === playerId || 
              p.customerProfileId === playerId || 
              p.userId === playerId
            );
            return found || playerRef;
          };

          return {
            ...match,
            player1: findPlayer(match.player1),
            player2: findPlayer(match.player2),
            winner: findPlayer(match.winner),
          };
        })
      };
    });
  }, [rounds, players]);

  const getPlayerName = (player: Player | null | undefined): string => {
    if (!player) return 'TBD';
    const name = player.name || player.fullName || `${player.firstName || ''} ${player.lastName || ''}`.trim() || 'TBD';
    // Truncate long names
    if (name.length > 20) {
      return name.substring(0, 17) + '...';
    }
    return name;
  };

  const getPlayerSkill = (player: Player | null | undefined): string => {
    if (!player || !player.skillLevel) return 'beginner';
    return player.skillLevel.toLowerCase();
  };

  const getMatchStatusColor = (status: string | undefined) => {
    switch (status?.toLowerCase()) {
      case 'completed': return '#22c55e';
      case 'ongoing':
      case 'in_progress': return '#ef4444';
      case 'pending':
      case 'scheduled': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  const getMatchStatusBgColor = (status: string | undefined) => {
    switch (status?.toLowerCase()) {
      case 'completed': return '#dcfce7';
      case 'ongoing':
      case 'in_progress': return '#fee2e2';
      case 'pending':
      case 'scheduled': return '#dbeafe';
      default: return '#f3f4f6';
    }
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 10, 200));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 10, 50));
  };

  const handleResetZoom = () => {
    setZoom(100);
  };

  // Organizer-style: constants and position calculation (centered between feeder matches)
  const MATCH_CARD_HEIGHT = 130; // Height reserved per card (content needs ~120px; extra avoids overlap)
  const MATCH_SPACING = 60; // Gap between match cards
  const TOTAL_MATCH_HEIGHT = MATCH_CARD_HEIGHT + MATCH_SPACING;
  const ROUND_GAP = 80;
  const HORIZONTAL_LINE_LENGTH = 24;
  const CONNECTOR_STROKE = '#94a3b8';
  const CARD_WIDTH = 280;

  const calculateMatchPosition = React.useCallback((roundIndex: number, matchIndex: number): number => {
    if (roundIndex === 0) {
      return matchIndex * TOTAL_MATCH_HEIGHT;
    }
    const prevRound = processedRounds[roundIndex - 1];
    const prevMatches = prevRound?.matches || [];
    const match1Index = 2 * matchIndex;
    const match2Index = 2 * matchIndex + 1;
    if (prevMatches.length > match2Index) {
      const pos1 = calculateMatchPosition(roundIndex - 1, match1Index);
      const pos2 = calculateMatchPosition(roundIndex - 1, match2Index);
      const center1 = pos1 + (MATCH_CARD_HEIGHT / 2);
      const center2 = pos2 + (MATCH_CARD_HEIGHT / 2);
      return ((center1 + center2) / 2) - (MATCH_CARD_HEIGHT / 2);
    }
    if (prevMatches.length > match1Index) {
      return calculateMatchPosition(roundIndex - 1, match1Index);
    }
    return matchIndex * TOTAL_MATCH_HEIGHT;
  }, [processedRounds]);

  // Calculate round status summary
  const roundStatusSummary = React.useMemo(() => {
    return processedRounds.map((round) => ({
      name: round.name,
      completed: round.completed || 0,
      total: round.total || 0
    }));
  }, [processedRounds]);

  if (processedRounds.length === 0) {
    return (
      <div className={styles.bracketContainer}>
        <div className={styles.noData}>
          <p>No tournament bracket data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.bracketContainer}>
      {/* Header with Current Status and History */}
      <div className={styles.bracketHeader}>
        <div className={styles.statusTabs}>
          <button className={`${styles.statusTab} ${styles.active}`}>
            <Check size={16} /> Current Status
          </button>
          <button className={styles.statusTab}>History 1</button>
        </div>
        
        {/* Round Status Summary */}
        <div className={styles.roundStatusSummary}>
          {roundStatusSummary.map((round, index) => (
            <div key={index} className={styles.roundStatusItem}>
              <span className={styles.roundStatusName}>{round.name}:</span>
              <span className={styles.roundStatusCount}>
                {round.completed}/{round.total} completed
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Tournament Title */}
      <div className={styles.tournamentTitleSection}>
        <h1 className={styles.tournamentTitle}>{tournamentName}</h1>
        <p className={styles.tournamentSubtitle}>Single Elimination Tournament Bracket</p>
      </div>

      {/* Zoom Controls */}
      <div className={styles.zoomControls}>
        <button onClick={handleZoomOut} className={styles.zoomButton}>-</button>
        <span className={styles.zoomValue}>{zoom}%</span>
        <button onClick={handleZoomIn} className={styles.zoomButton}>+</button>
        <button onClick={handleResetZoom} className={styles.resetButton}>Reset</button>
      </div>

      {/* Bracket Tree Container */}
      <div 
        className={styles.bracketTreeWrapper}
        style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top left' }}
      >
        <div className={styles.bracketTree} ref={bracketRef}>
          {processedRounds.map((round, roundIndex) => {
            const roundMatches = round.matches || [];
            const isLastRound = roundIndex === processedRounds.length - 1;
            const roundMatchesCount = roundMatches.length;
            const extraBottomPadding = 80;
            const headerHeight = 60;
            const actualRoundHeight = (roundMatchesCount * TOTAL_MATCH_HEIGHT) + extraBottomPadding + headerHeight;

            // Final positions with overlap prevention (organizer-style)
            const finalPositions: number[] = [];
            roundMatches.forEach((_match, matchIndex) => {
              let matchTop = calculateMatchPosition(roundIndex, matchIndex);
              for (let prevIndex = 0; prevIndex < matchIndex; prevIndex++) {
                const minRequired = finalPositions[prevIndex] + TOTAL_MATCH_HEIGHT;
                if (matchTop < minRequired) matchTop = minRequired;
              }
              finalPositions[matchIndex] = matchTop;
            });

            return (
              <div key={roundIndex} className={styles.roundColumn} style={{ minHeight: `${actualRoundHeight}px` }}>
                <div className={styles.roundHeader}>
                  <h3 className={styles.roundName}>{round.name}</h3>
                  {round.completed !== undefined && round.total !== undefined && (
                    <span className={styles.roundProgress}>
                      {round.completed}/{round.total} completed
                    </span>
                  )}
                </div>
                <div className={styles.matchesListAbsolute} style={{ minHeight: `${actualRoundHeight - headerHeight}px`, paddingBottom: '80px' }} data-round-naresh-id={round.id || round.round_id}>
                  {roundMatches.map((match, matchIndex) => {
                    const player1Name = getPlayerName(match.player1);
                    const player2Name = getPlayerName(match.player2);
                    const player1Skill = getPlayerSkill(match.player1);
                    const player2Skill = getPlayerSkill(match.player2);
                    const isPlayer1Winner = match.winner?.id === match.player1?.id || 
                                           match.winner?.customerProfileId === match.player1?.customerProfileId ||
                                           match.winner?.userId === match.player1?.userId;
                    const isPlayer2Winner = match.winner?.id === match.player2?.id || 
                                           match.winner?.customerProfileId === match.player2?.customerProfileId ||
                                           match.winner?.userId === match.player2?.userId;
                    const isCompleted = match.status === 'completed';
                    const isPending = match.status === 'pending' || match.status === 'scheduled';
                    const matchNumber = match.match_number || matchIndex + 1;
                    let finalMatchTop = finalPositions[matchIndex] ?? matchIndex * TOTAL_MATCH_HEIGHT;
                    //finalMatchTop = finalMatchTop + 20;
                    // SVG connector to next round (organizer-style: straight lines that merge)
                    const isFirstInPair = matchIndex % 2 === 0;
                    const nextRound = !isLastRound ? processedRounds[roundIndex + 1] : null;
                    const nextRoundMatches = nextRound?.matches || [];
                    const nextRoundMatchIndex = Math.floor(matchIndex / 2);
                    const hasNextMatchInRound = nextRoundMatches.length > nextRoundMatchIndex;

                    let connectorSvg: React.ReactNode = null;
                    if (!isLastRound && hasNextMatchInRound) {
                      const nextRoundMatchTop = calculateMatchPosition(roundIndex + 1, nextRoundMatchIndex);
                      const currentMatchCenter = finalMatchTop + (MATCH_CARD_HEIGHT / 2);
                      const nextMatchCenter = nextRoundMatchTop + (MATCH_CARD_HEIGHT / 2);
                      const pairMatchIndex = isFirstInPair ? matchIndex + 1 : matchIndex - 1;
                      const pairMatchTop = pairMatchIndex >= 0 && pairMatchIndex < roundMatches.length
                        ? (finalPositions[pairMatchIndex] ?? pairMatchIndex * TOTAL_MATCH_HEIGHT)
                        : finalMatchTop;
                      const pairMatchCenter = pairMatchTop + (MATCH_CARD_HEIGHT / 2);
                      const meetingPoint = (currentMatchCenter + pairMatchCenter) / 2;
                      const minY = Math.min(finalMatchTop, pairMatchTop, nextRoundMatchTop) - 10;
                      const maxY = Math.max(
                        finalMatchTop + MATCH_CARD_HEIGHT,
                        pairMatchTop + MATCH_CARD_HEIGHT,
                        nextRoundMatchTop + MATCH_CARD_HEIGHT
                      ) + 10;
                      const svgHeight = maxY - minY;
                      const lineExtension = 20;
                      const svgWidth = ROUND_GAP + lineExtension;
                      const toLocalY = (y: number) => y - minY;

                      connectorSvg = (
                        <svg
                          className={styles.connectorSvg}
                          style={{
                            left: `${CARD_WIDTH}px`,
                            top: `${minY - finalMatchTop}px`,
                            width: `${svgWidth}px`,
                            height: `${svgHeight}px`
                          }}
                        >
                          <line x1={0} y1={toLocalY(currentMatchCenter)} x2={HORIZONTAL_LINE_LENGTH} y2={toLocalY(currentMatchCenter)} stroke={CONNECTOR_STROKE} strokeWidth="1.5" />
                          <line x1={HORIZONTAL_LINE_LENGTH} y1={toLocalY(currentMatchCenter)} x2={HORIZONTAL_LINE_LENGTH} y2={toLocalY(meetingPoint)} stroke={CONNECTOR_STROKE} strokeWidth="1.5" />
                          {isFirstInPair && (
                            <>
                              <line x1={HORIZONTAL_LINE_LENGTH} y1={toLocalY(meetingPoint)} x2={ROUND_GAP} y2={toLocalY(meetingPoint)} stroke={CONNECTOR_STROKE} strokeWidth="1.5" />
                              <line x1={ROUND_GAP} y1={toLocalY(meetingPoint)} x2={ROUND_GAP} y2={toLocalY(nextMatchCenter)} stroke={CONNECTOR_STROKE} strokeWidth="1.5" />
                              <line x1={ROUND_GAP} y1={toLocalY(nextMatchCenter)} x2={ROUND_GAP + lineExtension} y2={toLocalY(nextMatchCenter)} stroke={CONNECTOR_STROKE} strokeWidth="1.5" />
                            </>
                          )}
                        </svg>
                      );
                    }

                    return (
                      <div
                      data-match-naresh-id={match.id || match.match_id}
                        key={match.id || match.match_id || matchIndex}
                        className={styles.matchWrapperAbsolute}
                        style={{ top: `${finalMatchTop}px`, left: 0 }}
                      >
                        {/* Connector from previous round (horizontal line into this match) */}
                       
                        {connectorSvg}
                        <div
                          className={`${styles.matchBox} ${
                            isCompleted ? styles.completed : 
                            match.status === 'ongoing' || match.status === 'in_progress' ? styles.ongoing : 
                            styles.pending
                          }`}
                          style={{ minHeight: `${MATCH_CARD_HEIGHT}px` }}                          
                        >
                          <div className={styles.matchNumber}>M{matchNumber}</div>
                          <div
                            className={styles.matchStatusBadgeRight}
                            style={{
                              backgroundColor: getMatchStatusBgColor(match.status),
                              color: getMatchStatusColor(match.status)
                            }}
                          >
                            {isCompleted ? 'Done' : match.status === 'ongoing' || match.status === 'in_progress' ? 'Live' : 'Pending'}
                          </div>
                          <div className={styles.matchContent}>
                            <div className={`${styles.playerBox} ${isCompleted ? styles.playerBoxCompleted : styles.playerBoxPending} ${isPlayer1Winner ? styles.winner : ''}`}>
                              <div className={styles.playerRow}>
                                <span className={styles.playerSlotNumber}>1</span>
                                <div className={styles.playerInfo}>
                                  {(player1Name !== 'TBD' && player1Name !== 'BYE' && player1Name !== 'BYE PLAYER') && (
                                    <img
                                      src={getAvatarFromPlayer(match.player1)}
                                      alt={player1Name}
                                      className={styles.playerAvatar}
                                      onError={(e) => {
                                        (e.currentTarget as HTMLImageElement).onerror = null;
                                        (e.currentTarget as HTMLImageElement).src = DEFAULT_AVATAR;
                                      }}
                                    />
                                  )}
                                  <div className={styles.playerDetails}>
                                    <span className={styles.playerName} title={getPlayerName(match.player1) === 'TBD' ? '' : (match.player1?.name || match.player1?.fullName || '')}>
                                      {player1Name === 'BYE' || player1Name === 'BYE PLAYER' ? 'BYE PLAYER' : player1Name}
                                      {isPlayer1Winner && <span className={styles.winnerBadge}>🏆</span>}
                                    </span>
                                    {player1Name !== 'TBD' && player1Name !== 'BYE' && player1Name !== 'BYE PLAYER' && (
                                      <span className={`${styles.playerSkill} ${styles[player1Skill]}`}>{player1Skill}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className={styles.vsDivider}>VS</div>
                            <div className={`${styles.playerBox} ${isCompleted ? styles.playerBoxCompleted : styles.playerBoxPending} ${isPlayer2Winner ? styles.winner : ''}`}>
                              <div className={styles.playerRow}>
                                <span className={styles.playerSlotNumber}>2</span>
                                <div className={styles.playerInfo}>
                                  {(player2Name !== 'TBD' && player2Name !== 'BYE' && player2Name !== 'BYE PLAYER') && (
                                    <img
                                      src={getAvatarFromPlayer(match.player2)}
                                      alt={player2Name}
                                      className={styles.playerAvatar}
                                      onError={(e) => {
                                        (e.currentTarget as HTMLImageElement).onerror = null;
                                        (e.currentTarget as HTMLImageElement).src = DEFAULT_AVATAR;
                                      }}
                                    />
                                  )}
                                  <div className={styles.playerDetails}>
                                    <span className={styles.playerName} title={getPlayerName(match.player2) === 'TBD' ? '' : (match.player2?.name || match.player2?.fullName || '')}>
                                      {player2Name === 'BYE' || player2Name === 'BYE PLAYER' ? 'BYE PLAYER' : player2Name}
                                      {isPlayer2Winner && <span className={styles.winnerBadge}>🏆</span>}
                                    </span>
                                    {player2Name !== 'TBD' && player2Name !== 'BYE' && player2Name !== 'BYE PLAYER' && (
                                      <span className={`${styles.playerSkill} ${styles[player2Skill]}`}>{player2Skill}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TournamentBracket;

